import { BehaviorSubject } from 'rxjs';
import { setToast } from '../store/ToastManagement';
import {
  obfuscate,
  elucidate,
  isAON,
  hubCustomerOnboarded,
  hubAccountOnboarded,
  aonCustomerOnboarded,
  aonAccountOnboarded
} from '../utils';
import { servicesAPI } from '../_services/services_api';
import { history } from '../_helpers';

const Config = require('../conf');

const getObjectFromSessionStorage = name => {
  let json = null;
  try {
    json = JSON.parse(elucidate(name));
  } catch (error) {
    sessionStorage.removeItem(name);
  }
  return json;
};

const loginUserSubject = new BehaviorSubject(
  getObjectFromSessionStorage('loginuser')
);
const currentUserSubject = new BehaviorSubject(
  getObjectFromSessionStorage('curruser')
);

// Auto detect iPhone users
const updateUserAgent = async customerId => {
  const values = {
    pref_user_agent: navigator.userAgent,
    id: customerId
  };

  await servicesAPI
    .CustomerServices('updatePreferences', values, customerId)
    .then(
      response => {},
      error => {}
    );
};

const getCookie = name => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2)
    return parts
      .pop()
      .split(';')
      .shift();
};

const authOptions = () => {
  const csrfToken = getCookie('csrf_token');
  // console.log(
  //   `CSRF token from cookie=${csrfToken}, document.cookie=${document.cookie}`
  // );

  return {
    credentials: 'include',
    headers: {
      'X-CSRF-TOKEN': csrfToken,
      'Content-Type': 'application/json'
    }
  };
};

const secureFetch = (requestURL, requestOptions) => {
  return fetch(requestURL, requestOptions);
};

const handleResponse = response => {
  if (response.status === 404) return null;

  return response.text().then(text => {
    if (text === 'Invalid IAP credentials: Unable to parse JWT') {
      console.log(text);
      window.location.reload();
      return {};
    }
    let data = {};

    const isJson = response.headers
      .get('content-type')
      .startsWith('application/json');
    if (isJson) {
      try {
        data = text && JSON.parse(text);
      } catch (error) {
        console.log(
          `Failed to parse API response (status=${response.status}) JSON: ${text}`
        );
      }
    } else {
      console.log(
        `Failed to parse API response (status=${response.status}): not JSON`
      );
    }

    if (!response.ok) {
      // Auto logout if 403 Forbidden response returned from api
      if (response.status === 403) {
        console.log(
          'RBAC/ACL resource access rejected for user (HTTP 403 response 403)'
        );
        authAPI.logout();
        // document.location.href = '/';
        return Promise.reject('RBAC/ACL resource access rejected for user');
      }

      const error = (data && data.message) || response.statusText;
      return Promise.reject(error);
    }

    return data;
  });
};

const handleResponseNoLogin = response => {
  // Ignore JWT and session as do not need to be logged in
  if (response.status === 404) return null;

  return response.text().then(text => {
    if (text === 'Invalid IAP credentials: Unable to parse JWT') {
      window.location.reload();
      return {};
    }
    let data = {};

    const isJson = response.headers
      .get('content-type')
      .startsWith('application/json');
    if (isJson) {
      try {
        data = text && JSON.parse(text);
      } catch (error) {
        console.log(
          `Failed to parse API response (status=${response.status}) JSON: ${text}`
        );
      }
    } else {
      console.log(
        `Failed to parse API response (status=${response.status}): not JSON`
      );
    }

    if (!response.ok) {
      window.location.reload();
      return {};
    }

    return data;
  });
};

const startLoginSession = (user, path, location, dispatch) => {
  // Delete temporary login user
  loginUserSubject.next(null);

  // Store logged in user in session storage
  obfuscate('curruser', JSON.stringify(user));
  currentUserSubject.next(user);

  // Check for returnUrl from query params (for admin portal redirect)
  const urlParams = new URLSearchParams(window.location.search);
  const returnUrl = urlParams.get('returnUrl');

  if (returnUrl) {
    // Redirect back to admin portal or wherever they came from
    document.location.href = decodeURIComponent(returnUrl);
    return;
  }

  if (path) {
    document.location.href = path;
    return;
  }

  if (user.roles.includes('Customer')) {
    if (dispatch) {
      dispatch(
        setToast({
          title: `Logging you in`,
          body: `Please wait...`
        })
      );
    }

    loginCustomer(user);
    return;
  }

  // Successful login
  const welcome =
    user.user_name === 'sandy'
      ? 'Welcome, Supreme High Commander'
      : 'Welcome to Trojan Energy';

  const displayMessage = (title, message, success) =>
    dispatch(
      setToast({
        title: title,
        success: success,
        body: message
      })
    );

  displayMessage(welcome, 'Loading admin dashboard', true);

  const redirect = location?.state ? location.state.from.pathname : null;
  document.location.href = redirect ? redirect : `/admin`;
};

const activate = (customerId, authToken, values) => {
  const requestOptions = {
    ...authOptions(),
    method: 'POST',
    body: JSON.stringify(values)
  };
  return secureFetch(
    Config.Local().customer_url +
      `/api/portal/customer/activate/${customerId}/${authToken}`,
    requestOptions
  ).then(handleResponse);
};

const loginPassword = (user_name, password, path, location, dispatch) => {
  const displayMessage = (title, message, success) =>
    dispatch(
      setToast({
        title: title,
        success: success,
        body: message
      })
    );

  const requestOptions = {
    ...authOptions(),
    method: 'POST',
    body: JSON.stringify({ user_name, password })
  };

  return secureFetch(
    Config.Local().customer_url + `/api/portal/user/login`,
    requestOptions
  )
    .then(handleResponse)
    .then(user => {
      obfuscate('loginuser', JSON.stringify(user));

      if (user['require_tfa']) document.location.href = `/login_tfa`;
      else startLoginSession(user, path, location, dispatch);
    })
    .catch(e => {
      displayMessage('Error', 'User name or password incorrect', false);
    });
};

const login2FA = (user_name, token, path, location, dispatch) => {
  const displayMessage = (title, message, success) =>
    dispatch(
      setToast({
        title: title,
        success: success,
        body: message
      })
    );

  const requestOptions = {
    ...authOptions(),
    method: 'POST',
    body: JSON.stringify({ user_name, token })
  };

  return secureFetch(
    Config.Local().customer_url + `/api/portal/user/login_tfa`,
    requestOptions
  )
    .then(handleResponse)
    .then(user => {
      obfuscate('curruser', JSON.stringify(user));
      loginUserSubject.next(null);

      startLoginSession(user, path, location, dispatch);
    })
    .catch(e => {
      displayMessage('Error', 'Incorrect token', false);
    });
};

const logout = () => {
  const requestOptions = {
    ...authOptions(),
    method: 'POST',
    body: JSON.stringify({})
  };

  secureFetch(
    Config.Local().customer_url + `/api/portal/user/logout`,
    requestOptions
  );

  // Remove user from local storage to log user out
  sessionStorage.removeItem('curruser');
  currentUserSubject.next(null);
  loginUserSubject.next(null);

  history.push('/login');
};

const routeHubCustomerOnLogin = (customer, account) => {
  hubCustomerOnboarded(customer);

  // Sign contract first
  if (!customer.agreed_to_terms_datetime)
    document.location.href = `/customer/terms`;
  // Force customers to fill out their contact & payment details
  else if (!hubAccountOnboarded(account))
    document.location.href = `/account/${customer.id}`;
  else document.location.href = `/customer/charging`;
};

const routeAonCustomerOnLogin = (customer, account) => {
  servicesAPI
    .AdminServices('getEntityState', 'AON_SIGN_UP', customer.id)
    .then(state => {
      servicesAPI.CustomerServices('getAonHost', customer.id).then(aonHost => {
        // Not onboarded unless they have an AON host set up
        if (aonHost) aonCustomerOnboarded(customer);

        // Sign contract first
        if (!customer.agreed_to_terms_datetime)
          document.location.href = `/customer/dashboard`;
        // Force customers to fill out their contact & payment details
        else if (!aonAccountOnboarded(customer, account))
          document.location.href = `/account/${customer.id}`;
        else document.location.href = `/customer/charging`;

        // Sign-up states
        // const onboarded = state?.approved_for_aon && aonHost?.hydra_tag_id;
        // if (!onboarded) document.location.href = `/customer/dashboard`;
        // else document.location.href = `/customer/charging`;
      });
    })
    .catch(error => {
      document.location.href = `/customer/dashboard`;
    });
};

export const loginCustomer = currentUser => {
  localStorage.removeItem('customerOnboarded');
  localStorage.removeItem('accountOnboarded');

  // Get customer
  servicesAPI.CustomerServices('getByUserId', currentUser.id).then(customer => {
    // Get billing account
    servicesAPI
      .BillingServices('getAccountByCustomerId', customer.id)
      .then(account => {
        // Before charging for the first time, get customers to:
        // - fill out contact details
        // - fill out payment details
        // - register lance
        servicesAPI
          .CustomerServices('getLanceCustomerIdsByCustomer', customer.id)
          .then(lanceToCustomers => {
            // Check if customer has an active lance
            // let lanceActivated = false;
            // lanceToCustomers.forEach(lanceToCustomer => {
            //   if (lanceToCustomer.status === 'ACTIVE')
            //     lanceActivated = true;
            // });

            updateUserAgent(customer.id);

            if (isAON(customer)) {
              routeAonCustomerOnLogin(customer, account);
            } else {
              routeHubCustomerOnLogin(customer, account);
            }
          });
      });
  });
};

const forgotPassword = user_name => {
  const requestOptions = {
    ...authOptions(),
    method: 'POST',
    body: JSON.stringify({
      user_name
    })
  };
  return secureFetch(
    Config.Local().customer_url + `/api/portal/user/forgot_password`,
    requestOptions
  ).then(handleResponse);
};

const resetForgottenPassword = (user_id, new_password, verification_code) => {
  const requestOptions = {
    ...authOptions(),
    method: 'PUT',
    body: JSON.stringify({
      id: user_id,
      new_password,
      verification_code
    })
  };
  return secureFetch(
    Config.Local().customer_url + `/api/portal/user/reset_forgotten_password`,
    requestOptions
  ).then(handleResponse);
};

const resetPassword = (user_id, old_password, new_password) => {
  const requestOptions = {
    ...authOptions(),
    method: 'PUT',
    body: JSON.stringify({
      id: user_id,
      old_password,
      new_password
    })
  };
  return secureFetch(
    Config.Local().customer_url + `/api/portal/user/reset_password`,
    requestOptions
  ).then(handleResponse);
};

const impersonateCustomer = customer => {
  console.log(
    `impersonateCustomer(): customerId=${customer.id}, userId=${customer.user_id}`
  );

  const requestOptions = {
    ...authOptions(),
    method: 'GET'
  };
  secureFetch(
    Config.Local().customer_url + `/api/portal/user/${customer.user_id}`,
    requestOptions
  )
    .then(handleResponse)
    .then(user => {
      console.log(`impersonateCustomer(): user=${user}`);

      const newUser = {
        ...currentUserSubject.value,
        impersonating: true,
        id: customer.user_id,
        roles: ['Customer'], // Explicitly only impersonate customers
        user_name: user.user_name,
        created_datetime: user.created_datetime
      };

      console.log(`impersonateCustomer(): newUser=${newUser}`);
      obfuscate('curruser', JSON.stringify(newUser));
      currentUserSubject.next(newUser);
    });
};

export const authAPI = {
  authOptions,
  secureFetch,
  handleResponse,
  handleResponseNoLogin,
  activate,
  loginPassword,
  login2FA,
  logout,
  loginCustomer,
  forgotPassword,
  resetForgottenPassword,
  resetPassword,
  impersonateCustomer,
  loginUser: loginUserSubject.asObservable(),
  currentUser: currentUserSubject.asObservable(),
  get currentUserValue() {
    return currentUserSubject.value;
  }
};
